import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Input, Textarea } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { can, useMe } from '../lib/session';
import { formatDateTime } from '../lib/utils';

interface Experiment {
  id: string;
  code: string;
  name: string;
  hypothesis: string | null;
  isActive: boolean;
  startedAt: string | null;
  endedAt: string | null;
  variants: { code: string; weight: number; config: Record<string, unknown> }[];
}

interface Results {
  srmPValue: number | null;
  srmProblem: boolean;
  variants: {
    code: string;
    visitors: number;
    conversions: number;
    conversionRate: number;
    interval: [number, number];
    upliftPct: number | null;
    pValue: number | null;
  }[];
}

const pct = (v: number) => `${(v * 100).toFixed(2)}%`;

function ResultsTable({ id }: { id: string }) {
  const { t } = useTranslation();
  const results = useQuery({
    queryKey: ['experiments', id, 'results'],
    queryFn: () => api<Results>(`/experiments/${id}/results`),
  });
  const r = results.data;
  if (!r) return null;
  return (
    <div className="grid gap-2">
      {r.srmProblem ? <Alert variant="destructive">{t('exp.srm')}</Alert> : null}
      <table className="w-full text-sm">
        <thead className="text-xs text-muted-foreground">
          <tr>
            <th className="text-left font-normal">{t('exp.variant')}</th>
            <th className="text-right font-normal">{t('exp.visitors')}</th>
            <th className="text-right font-normal">{t('exp.orders')}</th>
            <th className="text-right font-normal">{t('exp.conversion')}</th>
            <th className="text-right font-normal">{t('exp.uplift')}</th>
            <th className="text-right font-normal">p</th>
          </tr>
        </thead>
        <tbody>
          {r.variants.map((v) => (
            <tr key={v.code} className="border-t" data-testid={`variant-${v.code}`}>
              <td className="py-1 font-mono">{v.code}</td>
              <td className="text-right">{v.visitors}</td>
              <td className="text-right">{v.conversions}</td>
              <td className="text-right">
                {pct(v.conversionRate)}{' '}
                <span className="text-xs text-muted-foreground">
                  ({pct(v.interval[0])}–{pct(v.interval[1])})
                </span>
              </td>
              <td className="text-right">
                {v.upliftPct === null ? '—' : `${String(v.upliftPct)}%`}
              </td>
              <td className="text-right">
                {v.pValue === null ? '—' : v.pValue < 0.001 ? '<0.001' : v.pValue.toFixed(3)}
                {v.pValue !== null && v.pValue < 0.05 ? ' ✓' : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground">{t('exp.howToRead')}</p>
    </div>
  );
}

function NewExperiment({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [hypothesis, setHypothesis] = useState('');
  const [variants, setVariants] = useState('control 0.5 {}\nb 0.5 {}');
  const [error, setError] = useState<string>();
  const save = async () => {
    setError(undefined);
    try {
      const parsed = variants
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((line) => {
          const [variantCode = '', weight = '0', ...rest] = line.split(' ');
          return {
            code: variantCode,
            weight: Number(weight),
            config: JSON.parse(rest.join(' ') || '{}') as Record<string, unknown>,
          };
        });
      await api('/experiments', {
        method: 'POST',
        body: { code, name, ...(hypothesis ? { hypothesis } : {}), variants: parsed },
      });
      await queryClient.invalidateQueries({ queryKey: ['experiments'] });
      onClose();
    } catch (err) {
      setError(err instanceof SyntaxError ? t('exp.badJson') : errorMessage(err));
    }
  };
  const text = (label: string, value: string, set: (v: string) => void) => (
    <Field label={label}>
      {(id) => (
        <Input
          id={id}
          value={value}
          onChange={(e) => {
            set(e.target.value);
          }}
        />
      )}
    </Field>
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('exp.new')}
      className="max-w-xl"
      footer={<Button onClick={() => void save()}>{t('common.save')}</Button>}
    >
      <div className="grid gap-3 text-sm">
        {text(t('exp.code'), code, setCode)}
        {text(t('exp.name'), name, setName)}
        <Field label={t('exp.hypothesis')}>
          {(id) => (
            <Textarea
              id={id}
              rows={2}
              value={hypothesis}
              onChange={(e) => {
                setHypothesis(e.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('exp.variants')} hint={t('exp.variantsHint')}>
          {(id) => (
            <Textarea
              id={id}
              rows={4}
              value={variants}
              onChange={(e) => {
                setVariants(e.target.value);
              }}
              className="font-mono"
            />
          )}
        </Field>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}

/** A/B-тесты: гипотеза, варианты, запуск, итоги с интервалами и проверкой SRM. */
export function ExperimentsPage() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['experiments'],
    queryFn: () => api<{ items: Experiment[] }>('/experiments'),
  });
  const [creating, setCreating] = useState(false);
  const manage = can(me, 'experiments.manage');
  const toggle = async (e: Experiment) => {
    await api(`/experiments/${e.id}/${e.isActive ? 'stop' : 'start'}`, { method: 'POST' });
    await queryClient.invalidateQueries({ queryKey: ['experiments'] });
  };

  return (
    <>
      <PageHeader
        title={t('exp.title')}
        description={t('exp.hint')}
        actions={
          manage ? (
            <Button
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('exp.new')}
            </Button>
          ) : null
        }
      />
      {list.data?.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('exp.empty')}</p>
      ) : null}
      <div className="grid gap-4">
        {(list.data?.items ?? []).map((e) => (
          <Card key={e.id}>
            <CardHeader
              title={`${e.name} · ${e.code}`}
              description={e.hypothesis ?? ''}
              actions={
                <span className="flex items-center gap-2">
                  <Badge variant={e.isActive ? 'secondary' : 'outline'}>
                    {e.isActive
                      ? t('exp.running')
                      : e.startedAt
                        ? t('exp.stopped')
                        : t('exp.draft')}
                  </Badge>
                  {manage ? (
                    <Button size="sm" variant="outline" onClick={() => void toggle(e)}>
                      {e.isActive ? t('exp.stop') : t('exp.start')}
                    </Button>
                  ) : null}
                </span>
              }
            />
            <CardContent className="grid gap-2 text-sm">
              <p className="text-muted-foreground">
                {e.variants
                  .map((v) => `${v.code} ${String(Math.round(v.weight * 100))}%`)
                  .join(' · ')}
                {e.startedAt
                  ? ` · ${t('exp.since')} ${formatDateTime(e.startedAt, i18n.language)}`
                  : ''}
              </p>
              {e.startedAt ? <ResultsTable id={e.id} /> : null}
            </CardContent>
          </Card>
        ))}
      </div>
      {creating ? (
        <NewExperiment
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </>
  );
}
